import { createSlice } from "@reduxjs/toolkit";
import {
  createPlantelThunk,
  getPlantelByIdThunk,
  getPlantelsThunk,
  togglePlantelActiveThunk,
  updatePlantelThunk,
} from "./plantels.thunks";

const initialState = {
  list: [],
  loading: false,
  error: null,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  count: 0,
  next: null,
  previous: null,
  maxPageReached: 1,
  search: "",

  selectedPlantel: null,
  selectedPlantelLoading: false,
  selectedPlantelError: null,
  viewModalOpen: false,

  formModalOpen: false,
  formMode: "create", // "create" | "edit"
  formLoading: false,
  formError: null,

  confirmModalOpen: false,
  togglePlantelId: null,
  toggling: false,
};

const plantelsSlice = createSlice({
  name: "plantels",
  initialState,
  reducers: {
    clearPlantelsError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
      state.page = 1;
    },
    openViewModal: (state) => {
      state.viewModalOpen = true;
    },
    closeViewModal: (state) => {
      state.viewModalOpen = false;
      state.selectedPlantel = null;
    },
    openFormModal: (state, action) => {
      state.formModalOpen = true;
      state.formMode = action.payload?.mode || "create";
      if (action.payload?.plantel) {
        state.selectedPlantel = action.payload.plantel;
      }
      state.formError = null;
    },
    closeFormModal: (state) => {
      state.formModalOpen = false;
      state.selectedPlantel = null;
      state.formError = null;
    },
    openConfirmToggle: (state, action) => {
      state.confirmModalOpen = true;
      state.togglePlantelId = action.payload.id;
    },
    closeConfirmToggle: (state) => {
      state.confirmModalOpen = false;
      state.togglePlantelId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL
      .addCase(getPlantelsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPlantelsThunk.fulfilled, (state, action) => {
        const { results, count, next, previous } = action.payload;

        state.loading = false;
        state.list = results;
        state.count = count;
        state.next = next;
        state.previous = previous;

        state.maxPageReached = Math.max(state.maxPageReached, state.page);
      })
      .addCase(getPlantelsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createPlantelThunk.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(createPlantelThunk.fulfilled, (state, action) => {
        state.formLoading = false;
        state.list.unshift(action.payload);
        state.formModalOpen = false;
      })
      .addCase(createPlantelThunk.rejected, (state, action) => {
        state.formLoading = false;
        state.formError = action.payload;
      })

      // GET BY ID
      .addCase(getPlantelByIdThunk.pending, (state) => {
        state.selectedPlantelLoading = true;
        state.selectedPlantelError = null;
      })
      .addCase(getPlantelByIdThunk.fulfilled, (state, action) => {
        state.selectedPlantelLoading = false;
        state.selectedPlantel = action.payload;
      })
      .addCase(getPlantelByIdThunk.rejected, (state, action) => {
        state.selectedPlantelLoading = false;
        state.selectedPlantelError = action.payload;
      })

      // UPDATE
      .addCase(updatePlantelThunk.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(updatePlantelThunk.fulfilled, (state, action) => {
        state.formLoading = false;
        const index = state.list.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        state.formModalOpen = false;
      })
      .addCase(updatePlantelThunk.rejected, (state, action) => {
        state.formLoading = false;
        state.formError = action.payload;
      })

      // TOGGLE ACTIVE
      .addCase(togglePlantelActiveThunk.pending, (state) => {
        state.toggling = true;
      })
      .addCase(togglePlantelActiveThunk.fulfilled, (state, action) => {
        state.toggling = false;

        const id = state.togglePlantelId;
        const plantel = state.list.find((p) => p.id === id);
        if (plantel) {
          plantel.is_active = action.payload.is_active;
        }

        if (state.selectedPlantel?.id === id) {
          state.selectedPlantel.is_active = action.payload.is_active;
        }

        state.confirmModalOpen = false;
      })
      .addCase(togglePlantelActiveThunk.rejected, (state) => {
        state.toggling = false;
      });
  },
});

export const {
  clearPlantelsError,
  setPage,
  setSearch,
  openViewModal,
  closeViewModal,
  openFormModal,
  closeFormModal,
  openConfirmToggle,
  closeConfirmToggle,
} = plantelsSlice.actions;
export default plantelsSlice.reducer;
